import { AfterViewInit, Component, ElementRef, OnDestroy, inject, output, viewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputComponent } from '../input/input.component';
import { AuthService } from '../../core/services/auth.service';
import { Subscription } from 'rxjs';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { ErrorsService } from '../../core/services/errors.service';
import { ButtonType } from '../accent-button/button-types.enum';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { AuditService } from '../../core/services/audit.service';
import { A11yModule } from '@angular/cdk/a11y';
import { FocusService } from '../../core/services/focus.service';

@Component({
  selector: 'cwo-forgot-password-form',
  standalone: true,
  imports: [
    FormsModule,
    InputComponent,
    ReactiveFormsModule,
    AsyncButtonDirective,
    IconComponent,
    TranslocoModule,
    A11yModule
  ],
  templateUrl: './forgot-password-form.component.html',
  styleUrl: './forgot-password-form.component.scss',
})
export class ForgotPasswordFormComponent implements AfterViewInit, OnDestroy {
  authService = inject(AuthService);
  errorsService = inject(ErrorsService);
  modalService = inject(ModalService);
  private translocoService = inject(TranslocoService);
  private auditService = inject(AuditService);
  private focusService = inject(FocusService);

  loadingSubscription: Subscription | null = null;
  showEmailError = false;

  iconNameType = IconNameType;
  buttonType = ButtonType;
  allErrors?: string[] = [];

  forgotPasswordForm = new FormGroup({
    email: new FormControl(
      '',
      Validators.compose([Validators.required, Validators.email])
    ),
  });

  get email() {
    return this.forgotPasswordForm.controls.email;
  }

  readonly focusTarget = viewChild.required<ElementRef<HTMLElement>>('focusTarget');

  ngAfterViewInit(): void {
    this.focusService.savePreviousFocus();

    setTimeout(() => {
      this.focusService.focus(this.focusTarget().nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.focusService.restoreFocus();
  }

  readonly backToSignInClicked = output<string>();

  onBackToSignInClicked() {
    this.auditService.createAudit(() => 'Back To Sign In button clicked');
    this.backToSignInClicked.emit('Sign In');
  }

  enableErrors() {
    this.showEmailError = true;
  }

  onSubmit() {
    this.auditService.createAudit(() => 'Submit button clicked on forgot password form');
    
    if (!this.forgotPasswordForm.valid) {
      
      if (this.email.hasError('required')) 
        this.auditService.createAudit(() => `Sending the reset password email failed: ${this.translocoService.translate("route.enjoy.promotions.validations.email.required")}`);
      else if (this.email.hasError('email')) 
        this.auditService.createAudit(() => `Sending the reset password email failed: ${this.translocoService.translate("route.enjoy.promotions.validations.email.invalid")}`);

      this.enableErrors();
      return;
    }
    this.allErrors = [];
    if (this.forgotPasswordForm.value.email) {
      this.loadingSubscription = this.authService
        .forgotPassword(this.forgotPasswordForm.value.email)
        .subscribe({
          next: (serverMessage: any) => {
            this.loadingSubscription = null;
            if (!serverMessage?.success) {
              this.allErrors = [serverMessage?.message];
            } else {
              this.modalService.closeModal().subscribe({
                complete: () => {
                  this.auditService.createAudit(() => `Email has been sent to ${this.forgotPasswordForm.value.email} reset the password`);
                  this.modalService.openGenericModal({
                    modalTitle: 'Forgot Password',
                    modalMessage: serverMessage?.message,
                    confirmButtonText: "Back to Sign In",
                    closeOnAction: false,
                    primaryAction: () => {
                      this.modalService.closeModal().subscribe({
                        complete: () => {
                          this.auditService.createAudit(() => 'Back To Sign In button clicked');
                          this.modalService.openModal(ModalName.SignIn)
                        }
                      });

                    }
                  });
                }
              });

              //here we should emit an event on login to close the modal or do something else
            }
          },
          error: (err) => {
            this.loadingSubscription = null;
            if (!err.error) {
              this.allErrors = ['Network error'];
            } else {
              const parsedError = this.errorsService.parseErrorResponse(err);
              this.allErrors = parsedError.allErrors;
            }
          },
        });
    }
  }
}
